import { lazy, Suspense, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import PageLoader from "@/components/PageLoader";
import VersionChecker from "@/components/VersionChecker";

// Activation Lock - Bloquea toda la app hasta activación
import ActivationLock, { ACTIVATION_LOCKED } from "@/components/ActivationLock";

// Auth Context - NOT lazy loaded (needed immediately)
import { AuthProvider, useAuth } from "@/context/AuthContext";

// Landing page - NOT lazy loaded (first thing users see)
import LandingPage from "@/pages/LandingPage";

// All other pages - LAZY LOADED (load only when needed)
const StudentLogin = lazy(() => import("@/pages/StudentLogin"));
const StudentDashboard = lazy(() => import("@/pages/StudentDashboard"));
const TeacherLogin = lazy(() => import("@/pages/TeacherLogin"));
const TeacherRegister = lazy(() => import("@/pages/TeacherRegister"));
const TeacherDashboard = lazy(() => import("@/pages/TeacherDashboard"));
// DEPRECATED: ParentRegister.js ya no se usa - usar ParentRegisterV2
// const ParentRegister = lazy(() => import("@/pages/ParentRegister"));
const StudentRegister = lazy(() => import("@/pages/StudentRegister"));
const UserTypeSelector = lazy(() => import("@/pages/UserTypeSelector"));
const ParentRegisterV2 = lazy(() => import("@/pages/ParentRegisterV2"));
const ParentDashboardV2 = lazy(() => import("@/pages/ParentDashboardV2"));
const AdminLogin = lazy(() => import("@/pages/AdminLogin"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const SuperAdminLogin = lazy(() => import("@/pages/SuperAdminLogin"));
const SuperAdminSetup = lazy(() => import("@/pages/SuperAdminSetup"));
const SuperAdminDashboard = lazy(() => import("@/pages/SuperAdminDashboard"));
const CasinoMonitor = lazy(() => import("@/pages/CasinoMonitor"));
const PaymentSuccess = lazy(() => import("@/pages/PaymentSuccess"));
const MenuMonitor = lazy(() => import("@/pages/MenuMonitor"));
const EntregasMonitor = lazy(() => import("@/pages/EntregasMonitor"));
const InstructivoPage = lazy(() => import("@/pages/InstructivoPage"));
// DESACTIVADO: Módulo de quioscos deshabilitado para producción
// const KioscoAdminLogin = lazy(() => import("@/pages/KioscoAdminLogin"));
// const KioscoAdminDashboard = lazy(() => import("@/pages/KioscoAdminDashboard"));
// const KioscoTienda = lazy(() => import("@/pages/KioscoTienda"));

const ProtectedRoute = ({ children, allowedTypes }) => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAuthenticated) {
      const returnUrl = sessionStorage.getItem("monitor_return_url");
      if (returnUrl) {
        sessionStorage.removeItem("monitor_return_url");
        navigate(returnUrl, { replace: true });
      } else {
        navigate("/", { replace: true });
      }
    }
  }, [isAuthenticated, navigate]);

  if (!isAuthenticated) return null;

  if (allowedTypes && !allowedTypes.includes(user?.user_type)) {
    return <Navigate to="/" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        
        {/* Redirect /login → selector de tipo de usuario */}
        <Route path="/login" element={<Navigate to="/register/type" replace />} />

        {/* Student Routes */}
        <Route path="/student/login" element={<StudentLogin />} />
        {/* DEPRECATED: Rutas viejas redirigen a V2 */}
        <Route path="/parent/register" element={<ParentRegisterV2 />} />
        
        {/* V2 Registration Routes */}
        <Route path="/register/type" element={<UserTypeSelector />} />
        <Route path="/register/parent-v2" element={<ParentRegisterV2 />} />
        <Route path="/register/student" element={<StudentRegister />} />
        
        {/* V2 Parent Dashboard */}
        <Route 
          path="/parent/dashboard-v2" 
          element={
            <ProtectedRoute allowedTypes={["parent"]}>
              <ParentDashboardV2 />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/student/dashboard" 
          element={
            <ProtectedRoute allowedTypes={["student", "parent"]}>
              <StudentDashboard />
            </ProtectedRoute>
          } 
        />
        
        {/* Teacher Routes */}
        <Route path="/teacher/login" element={<TeacherLogin />} />
        <Route path="/teacher/register" element={<TeacherRegister />} />
        <Route 
          path="/teacher/dashboard" 
          element={
            <ProtectedRoute allowedTypes={["teacher"]}>
              <TeacherDashboard />
            </ProtectedRoute>
          } 
        />
        
        {/* Admin Routes */}
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route 
          path="/admin/dashboard" 
          element={
            <ProtectedRoute allowedTypes={["admin", "super_admin"]}>
              <AdminDashboard />
            </ProtectedRoute>
          } 
        />

        {/* Super Admin Routes */}
        <Route path="/super-admin/login" element={<SuperAdminLogin />} />
        <Route path="/super-admin/setup" element={<SuperAdminSetup />} />
        <Route 
          path="/super-admin/dashboard" 
          element={
            <ProtectedRoute allowedTypes={["super_admin"]}>
              <SuperAdminDashboard />
            </ProtectedRoute>
          } 
        />

        {/* Monitor - Public menu display with photos */}
        <Route path="/monitor/:slug" element={<MenuMonitor />} />
        
        {/* Kiosko - Self-service kiosk: PIN → Reprint vouchers */}
        <Route path="/kiosko/:slug" element={<CasinoMonitor />} />
        
        {/* Entregas - Operator delivery station: PIN/QR → Mark delivered */}
        <Route path="/entregas/:casinoSlug" element={<EntregasMonitor />} />
        
        {/* Instructivo - Public guide page */}
        <Route path="/instructivo" element={<InstructivoPage />} />
        
        {/* DESACTIVADO: Módulo de quioscos deshabilitado para producción */}
        
        {/* Payment Success Page */}
        <Route path="/payment/success" element={<PaymentSuccess />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        {/* BLOQUEO DE ACTIVACIÓN - Quitar cuando paguen */}
        {ACTIVATION_LOCKED && <ActivationLock />}
        
        <AppRoutes />
        <Toaster position="top-right" richColors />
        <VersionChecker />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
